import { toggleScrollBar } from "~~/utilities/toggleScrollBar";

export const useFilters = defineStore("useFilters", {
  state: () => ({
    activeFilters: [] as FilterGroup[],
    openFilterSideDrawer: false,
    activeFilterSideDrawerTab: null as FilterCategory | null,
  }),

  getters: {
    getFilters(): FilterGroup[] {
      return this.activeFilters;
    },
    getNumberOfActiveFilters(): number {
      return this.activeFilters.map((filter) => filter.filters.length).reduce((a, b) => a + b, 0);
    },
    getNumberOfActiveCategories(): number {
      return this.activeFilters.map((filter) => filter.filters.length).filter((filter) => filter > 0).length;
    },
    getActiveSubFilters(): string[][] {
      return this.activeFilters.map((filter) => filter.filters);
    },

    getActiveSubFiltersCount(): number[] {
      return this.activeFilters.map((filter) => filter.filters.length);
    },
    getTotalActiveSubFiltersCount(): number {
      return this.activeFilters.map((filter) => filter.filters.length).reduce((a, b) => a + b, 0);
    },
  },

  actions: {
    updateFilters(filters: { id: number; slug: string; name: string; filters: string[] }) {
      const filter = this.activeFilters.find((filter) => filter.id === filters.id);
      if (filter) {
        if (filters.filters.length === 0) {
          this.clearAllCategoryFilters(filters.id);
          return;
        }
        filter.filters = filters.filters;
      } else {
        this.activeFilters.push(filters);
      }
    },

    getActiveCategoryFilters(filterCategory: FilterCategory) {
      const activeFilters = this.activeFilters.find((filter) => filter.id === filterCategory.id);
      if (activeFilters) {
        return filterCategory.filters?.filter((filter) => activeFilters.filters.includes(filter.slug));
      }
      return [];
    },

    activateFilter(filterCategory: FilterCategory | null, filter: Filter) {
      if (filterCategory === null) return;
      const filterGroup = this.activeFilters.find((filter) => filter.id === filterCategory.id);
      if (filterGroup) {
        if (filterGroup.filters.includes(filter.slug)) {
          filterGroup.filters = filterGroup.filters.filter((filter2) => filter2 !== filter.slug);
        } else {
          filterGroup.filters.push(filter.slug);
        }
      } else {
        this.activeFilters.push({
          id: filterCategory.id,
          slug: filterCategory.slug,
          name: filterCategory.name,
          filters: [filter.slug],
        });
      }
    },

    isFilterActive(filterCategory: FilterCategory | null, filter: Filter) {
      if (filterCategory === null) return false;
      const filterGroup = this.activeFilters.find((filter) => filter.id === filterCategory.id);
      if (filterGroup) {
        return filterGroup.filters.includes(filter.slug);
      }
      return false;
    },

    addAllFilters(categoryId: number, categorySlug: string, filters: Filter[]) {
      const filter = this.activeFilters.find((filter) => filter.id === categoryId);
      const filterArray = filters.map((filter) => filter.slug);

      if (filter) {
        filter.filters = filterArray;
      } else {
        this.activeFilters.push({ id: categoryId, slug: categorySlug, name: "", filters: filterArray });
      }
    },

    clearAllCategoryFilters(categoryId: number) {
      const filter = this.activeFilters.find((filter) => filter.id === categoryId);

      if (filter) {
        this.activeFilters = this.activeFilters.filter((filter) => filter.id !== categoryId);
      }
    },
    toggleFiltersSideDrawer(value: boolean) {
      //if value is true measure scrollbar width add it as padding to body and set overflow to hidden
      //if value is false remove padding from body and set overflow to auto

      toggleScrollBar(value);

      this.openFilterSideDrawer = value;
    },
    clearAllFilters() {
      this.activeFilters = [];
    },
    clearActiveFilterTab() {
      this.activeFilterSideDrawerTab = null;
    },
    setActiveFilterTab(tab: FilterCategory) {
      this.activeFilterSideDrawerTab = tab;
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFilters, import.meta.hot));
}
